import { ControlChartLimitsApi } from "src/api/generated/spc/controlChart/api/controlChartLimitsApi.ts";
import { ClientSideDataGridModel } from "src/components/common/dataGrid/gridModel/ClientSideDataGridModel.tsx";
import { InlineCrudDataGrid } from "src/components/common/dataGrid/crud/InlineCrudDataGrid.tsx";
import { dateTimeColumn, floatColumn, integerColumn, textColumn } from "src/components/common/dataGrid/columns.tsx";
import { AsyncButton } from "src/components/common/buttons/AsyncButton.tsx";
import { faCalculator } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import { openFormOnDialog } from "src/components/common/dialogs/formDialog/openFormOnDialog.ts";
import { ControlChartLimitCalculationForm } from "./ControlChartLimitCalculationForm.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import i18n from "i18next";
import { setColumnVisibility } from "src/components/common/dataGrid/utils.ts";
import { controlChartLimitCalculationFormDialogContextKey } from "./controlChartAnalysisAgentContextKeys.ts";

export interface ControlChartLimitsDataGridProps {
	controlChartId: number;
}

type LimitRow = Awaited<ReturnType<typeof ControlChartLimitsApi.getLimits>>[number];

export function ControlChartLimitsDataGrid({ controlChartId }: ControlChartLimitsDataGridProps) {
	const { openDialog } = useGenericDialog();
	return (
		<ClientSideDataGridModel
			gridId={"D17AD152C3714C20"}
			fetchData={() => ControlChartLimitsApi.getLimits({ controlChartId })}
			initialParams={{}}
			getRows={(data) => data}
			getRowId={(row) => row.limitsId}
			render={({ dataGridProps: { initialState, ...dataGridProps } }) => (
				<InlineCrudDataGrid<LimitRow>
					initialState={setColumnVisibility(initialState, "limitsId", false)}
					columns={[
						integerColumn({ field: "limitsId", headerName: i18n.t("id") }),
						dateTimeColumn({ field: "usageStart", headerName: i18n.t("start_time") }),
						textColumn({ field: "note", headerName: i18n.t("note"), editable: true, width: 300 }),
						floatColumn({ field: "xLcl", headerName: "LCL" }),
						floatColumn({ field: "xAverage", headerName: "X" }),
						floatColumn({ field: "xUcl", headerName: "UCL" }),
						floatColumn({ field: "rLcl", headerName: "LCLr" }),
						floatColumn({ field: "rAverage", headerName: "R" }),
						floatColumn({ field: "rUcl", headerName: "UCLr" }),
					]}
					save={{
						type: "enabled",
						action: async ({ items }) => {
							await Promise.all(
								items.map((item) =>
									ControlChartLimitsApi.updateLimitNote({
										limitsId: item.limitsId,
										note: item.note,
									}),
								),
							);
							return items;
						},
					}}
					remove={{
						type: "enabled",
						action: async ({ items }) => {
							await Promise.all(
								items.map((item) => ControlChartLimitsApi.deleteLimits({ limitsId: item.limitsId })),
							);
						},
					}}
					actionBarComponents={
						<AsyncButton
							icon={faCalculator}
							label={i18n.t("calculate_control_limits")}
							onClick={() =>
								openFormOnDialog({
									openDialog,
									title: i18n.t("calculate_control_limits"),
									size: "lg",
									component: ControlChartLimitCalculationForm,
									props: { controlChartId },
									onSubmit: dataGridProps.refreshData,
									agentContext: {
										key: controlChartLimitCalculationFormDialogContextKey,
										data: { controlChartId },
									},
								})
							}
						/>
					}
					agentContext={{
						key: defineAgentUiContextKey("spc.controlChart.limits.grid"),
						data: { controlChartId },
						rowFields: ["limitsId", "usageStart", "note"],
						selectedRowPathSegment: (row) => `${i18n.t("id")} ${row.limitsId}`,
						selectedRowsPathSegment: (count) => i18n.t("rows_selected_count", { count }),
					}}
					{...dataGridProps}
				/>
			)}
		/>
	);
}
